import { Badge, Button, type TableColumn } from "../../ds";
import type { MwanInterface, MwanMember, MwanPolicy, MwanRule, MwanStatus } from "../../lib/api";
import type { FieldSpec } from "./EntryModal";

// Was an mwan3 einstellbar ist, an EINER Stelle beschrieben — Spalten für die
// Tabelle, Felder fürs Formular, Leerzeile für „neu", und der Param-Name auf dem
// Draht (gelesen wird camelCase, `mwan_set` nimmt snake_case).
//
// Die vier Ebenen ergeben nur zusammen einen Sinn: eine REGEL zeigt auf eine
// POLICY, eine Policy listet MEMBER, ein Member zeigt auf ein INTERFACE. In
// dieser Reihenfolge stehen sie auch in der Oberfläche — von „welcher Verkehr"
// bis „welche Leitung".

export type MwanEntry = MwanInterface | MwanMember | MwanPolicy | MwanRule;
export type MwanKey = "interfaces" | "members" | "policies" | "rules";

/** Anzeige-Reihenfolge: die Leitungen zuerst, die Feinheiten danach. */
export const MWAN_KEYS: MwanKey[] = ["interfaces", "members", "policies", "rules"];

export interface MwanSpec<T extends MwanEntry> {
  key: MwanKey;
  /** uci-Sectionart — steht in den Fehlermeldungen des Servers. */
  title: string;
  singular: string;
  description: string;
  blank: T;
  fields: FieldSpec[];
  columns: TableColumn<T>[];
  empty: string;
  /** Lösch-Liste in den `mwan_set`-Params. */
  removeKey: string;
}

const LAST_RESORT = ["unreachable", "blackhole", "default"];
const PROTOCOLS = ["all", "tcp", "udp", "icmp"];

function actionColumn<T extends MwanEntry>(
  onEdit: (entry: T, index: number) => void,
  onDelete: (index: number) => void,
): TableColumn<T> {
  return {
    key: "edit",
    header: "",
    align: "right",
    className: "actions",
    cell: (entry, index) => (
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" iconName="edit" onClick={() => onEdit(entry, index)}>
          Bearbeiten
        </Button>
        <Button size="sm" variant="danger-outline" iconName="trash" onClick={() => onDelete(index)}>
          Löschen
        </Button>
      </div>
    ),
  };
}

/** Der uci-Sectionname. Bestehende Zeilen tragen ihn als `id`, neue benennen
 *  sich über `name` — beim Speichern wird daraus das eine oder das andere. */
const nameColumn = <T extends MwanEntry>(header: string): TableColumn<T> => ({
  key: "id",
  header,
  mono: true,
  cell: (e) => e.id || <span className="text-content-3">neu</span>,
});

function interfaceSpec(
  onEdit: (entry: MwanInterface, index: number) => void,
  onDelete: (index: number) => void,
): MwanSpec<MwanInterface> {
  return {
    key: "interfaces",
    removeKey: "removeInterfaces",
    title: "Überwachte Leitungen",
    singular: "Leitung",
    description: "Woran mwan3 merkt, dass ein Uplink ausgefallen ist",
    blank: { id: "", enabled: true, trackIps: [], reliability: 1 },
    fields: [
      { key: "id", label: "Interface", kind: "text", help: "Name aus /etc/config/network" },
      { key: "enabled", label: "Überwacht", kind: "bool" },
      {
        key: "trackIps",
        label: "Prüf-Ziele",
        kind: "list",
        wire: "track_ips",
        help: "kommagetrennt, z.B. 1.1.1.1, 9.9.9.9",
      },
      {
        key: "reliability",
        label: "Nötige Treffer",
        kind: "number",
        help: "So viele Ziele müssen antworten",
      },
      { key: "count", label: "Pings je Runde", kind: "number" },
      { key: "timeout", label: "Timeout (s)", kind: "number" },
      { key: "interval", label: "Intervall (s)", kind: "number" },
      { key: "down", label: "Runden bis „tot“", kind: "number" },
      { key: "up", label: "Runden bis „lebt“", kind: "number" },
    ],
    columns: [
      {
        key: "enabled",
        header: "Überwacht",
        cell: (i) =>
          i.enabled ? (
            <Badge variant="success" dot>
              an
            </Badge>
          ) : (
            <Badge variant="neutral">aus</Badge>
          ),
      },
      nameColumn("Interface"),
      {
        key: "track",
        header: "Prüf-Ziele",
        mono: true,
        cell: (i) => (i.trackIps ?? []).join(", ") || "—",
      },
      { key: "reliability", header: "Treffer", num: true, cell: (i) => i.reliability ?? "—" },
      {
        key: "timing",
        header: "down / up",
        hideOnMobile: true,
        cell: (i) => `${i.down ?? "—"} / ${i.up ?? "—"}`,
      },
      actionColumn(onEdit, onDelete),
    ],
    empty: "mwan3 ist installiert, aber keine Leitung eingetragen.",
  };
}

function memberSpec(
  onEdit: (entry: MwanMember, index: number) => void,
  onDelete: (index: number) => void,
): MwanSpec<MwanMember> {
  return {
    key: "members",
    removeKey: "removeMembers",
    title: "Member (Vorrang je Leitung)",
    singular: "Member",
    description: "Metrik entscheidet den Vorrang — kleiner gewinnt; Gewicht verteilt die Last",
    blank: { id: "", interface: "", metric: 1, weight: 1 },
    fields: [
      { key: "id", label: "Name", kind: "text", help: "Konvention: <iface>_m<metrik>_w<gewicht>" },
      { key: "interface", label: "Leitung", kind: "text" },
      { key: "metric", label: "Metrik", kind: "number", help: "kleiner = zuerst" },
      { key: "weight", label: "Gewicht", kind: "number" },
    ],
    columns: [
      nameColumn("Member"),
      { key: "iface", header: "Leitung", mono: true, cell: (m) => m.interface ?? "—" },
      {
        key: "metric",
        header: "Metrik",
        num: true,
        cell: (m) => (
          <Badge variant={m.metric === 1 ? "success" : "neutral"}>
            {m.metric ?? "—"}
            {m.metric === 1 ? " · zuerst" : ""}
          </Badge>
        ),
      },
      {
        key: "weight",
        header: "Gewicht",
        num: true,
        hideOnMobile: true,
        cell: (m) => m.weight ?? "—",
      },
      actionColumn(onEdit, onDelete),
    ],
    empty: "Keine Member — ohne einen davon routet mwan3 über keine Leitung.",
  };
}

function policySpec(
  onEdit: (entry: MwanPolicy, index: number) => void,
  onDelete: (index: number) => void,
): MwanSpec<MwanPolicy> {
  return {
    key: "policies",
    removeKey: "removePolicies",
    title: "Policies",
    singular: "Policy",
    description: "Welche Member in welcher Reihenfolge — und was gilt, wenn keiner mehr trägt",
    blank: { id: "", members: [], lastResort: "unreachable" },
    fields: [
      { key: "id", label: "Name", kind: "text" },
      { key: "members", label: "Member", kind: "list", help: "kommagetrennt" },
      {
        key: "lastResort",
        label: "Wenn keiner trägt",
        kind: "select",
        wire: "last_resort",
        options: LAST_RESORT,
      },
    ],
    columns: [
      nameColumn("Policy"),
      {
        key: "members",
        header: "Member",
        mono: true,
        cell: (p) => (p.members ?? []).join(", ") || "—",
      },
      {
        key: "last",
        header: "Notfall",
        hideOnMobile: true,
        cell: (p) => p.lastResort ?? "unreachable",
      },
      actionColumn(onEdit, onDelete),
    ],
    empty: "Keine Policies.",
  };
}

function ruleSpec(
  onEdit: (entry: MwanRule, index: number) => void,
  onDelete: (index: number) => void,
): MwanSpec<MwanRule> {
  return {
    key: "rules",
    removeKey: "removeRules",
    title: "Regeln",
    singular: "Regel",
    description: "Welcher Verkehr über welche Policy läuft",
    blank: { id: "", destIp: "0.0.0.0/0", proto: "all", usePolicy: "", sticky: false },
    fields: [
      { key: "id", label: "Name", kind: "text" },
      { key: "usePolicy", label: "Policy", kind: "text", wire: "use_policy" },
      { key: "srcIp", label: "Quell-IP", kind: "text", wire: "src_ip", help: "IP oder Netz" },
      { key: "srcPort", label: "Quell-Port", kind: "text", wire: "src_port" },
      { key: "destIp", label: "Ziel-IP", kind: "text", wire: "dest_ip" },
      { key: "destPort", label: "Ziel-Port", kind: "text", wire: "dest_port" },
      { key: "proto", label: "Protokoll", kind: "select", options: PROTOCOLS },
      {
        key: "sticky",
        label: "Sticky",
        kind: "bool",
        help: "Verbindungen desselben Rechners bleiben auf derselben Leitung",
      },
    ],
    columns: [
      nameColumn("Regel"),
      {
        key: "src",
        header: "Quelle",
        mono: true,
        cell: (r) => [r.srcIp, r.srcPort].filter(Boolean).join(" : ") || "alle",
      },
      {
        key: "dest",
        header: "Ziel",
        mono: true,
        cell: (r) => [r.destIp, r.destPort].filter(Boolean).join(" : ") || "alle",
      },
      { key: "proto", header: "Proto", hideOnMobile: true, cell: (r) => r.proto ?? "all" },
      { key: "policy", header: "Policy", cell: (r) => r.usePolicy ?? "—" },
      actionColumn(onEdit, onDelete),
    ],
    empty: "Keine Regeln — ohne eine davon leitet mwan3 keinen Verkehr um.",
  };
}

export function mwanSpecs(
  onEdit: (key: MwanKey, entry: MwanEntry, index: number) => void,
  onDelete: (key: MwanKey, index: number) => void,
): MwanSpec<MwanEntry>[] {
  const bind =
    <T extends MwanEntry>(key: MwanKey) =>
    (entry: T, index: number) =>
      onEdit(key, entry, index);
  const cut = (key: MwanKey) => (index: number) => onDelete(key, index);
  return [
    interfaceSpec(bind("interfaces"), cut("interfaces")),
    memberSpec(bind("members"), cut("members")),
    policySpec(bind("policies"), cut("policies")),
    ruleSpec(bind("rules"), cut("rules")),
  ] as MwanSpec<MwanEntry>[];
}

/** Die vier Listen aus dem Snapshot — fehlende zählen als leer. */
export function mwanLists(mwan: MwanStatus | undefined): Record<MwanKey, MwanEntry[]> {
  return {
    interfaces: mwan?.interfaces ?? [],
    members: mwan?.members ?? [],
    policies: mwan?.policies ?? [],
    rules: mwan?.rules ?? [],
  };
}
